import { useState, useEffect, useCallback } from "react";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

declare global {
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

interface UsePWAReturn {
  /** PWA로 실행 중인지 (standalone 모드) */
  isPWA: boolean;
  /** React Native WebView에서 실행 중인지 */
  isInApp: boolean;
  /** PWA 설치 가능 여부 (Android에서 beforeinstallprompt 이벤트 발생 시) */
  canInstall: boolean;
  /** iOS 기기인지 */
  isIOS: boolean;
  /** iOS 버전 (감지 가능한 경우) */
  iosVersion: number | null;
  /** PWA 설치 프롬프트 표시 (Android 전용) */
  promptInstall: () => Promise<boolean>;
  /** 일반 브라우저에서 접속 중인지 (앱도 아니고 PWA도 아닌 경우) */
  isBrowser: boolean;
}

export function usePWA(): UsePWAReturn {
  const [isPWA, setIsPWA] = useState(false);
  const [isInApp, setIsInApp] = useState(false);
  const [canInstall, setCanInstall] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [iosVersion, setIosVersion] = useState<number | null>(null);
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;

    // React Native WebView 감지
    const inApp = !!window.ReactNativeWebView;
    setIsInApp(inApp);

    // PWA (standalone) 모드 감지
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as Navigator & { standalone?: boolean }).standalone ===
        true;
    setIsPWA(standalone);

    // iOS 감지 및 버전 파악
    const userAgent = window.navigator.userAgent;
    const isIOSDevice = /iPad|iPhone|iPod/.test(userAgent);
    setIsIOS(isIOSDevice);

    if (isIOSDevice) {
      const match = userAgent.match(/OS (\d+)_/);
      if (match) {
        setIosVersion(parseInt(match[1], 10));
      }
    }

    // Service Worker 등록
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker
        .register("/sw.js")
        .then((registration) => {
          console.log("[PWA] Service Worker registered:", registration.scope);
        })
        .catch((error) => {
          console.error("[PWA] Service Worker registration failed:", error);
        });
    }

    // beforeinstallprompt 이벤트 리스너 (Android Chrome)
    const handleBeforeInstallPrompt = (e: BeforeInstallPromptEvent) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setCanInstall(true);
      console.log("[PWA] Install prompt available");
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    // 앱 설치 완료 감지
    window.addEventListener("appinstalled", () => {
      console.log("[PWA] App installed");
      setCanInstall(false);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      );
    };
  }, []);

  const promptInstall = useCallback(async (): Promise<boolean> => {
    if (!deferredPrompt) {
      console.log("[PWA] No install prompt available");
      return false;
    }

    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log("[PWA] Install prompt outcome:", outcome);

      if (outcome === "accepted") {
        setCanInstall(false);
        setDeferredPrompt(null);
        return true;
      }
      return false;
    } catch (error) {
      console.error("[PWA] Install prompt error:", error);
      return false;
    }
  }, [deferredPrompt]);

  const isBrowser = !isPWA && !isInApp;

  return {
    isPWA,
    isInApp,
    canInstall,
    isIOS,
    iosVersion,
    promptInstall,
    isBrowser,
  };
}
